import React from 'react';
import Icon from 'components/AppIcon';

const AnxietyAssessmentSection = ({ data = {}, onChange, errors = [] }) => {
  const handleInputChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleMultiSelectChange = (field, value, checked) => {
    const currentValues = data[field] || [];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(v => v !== value);
    onChange({ [field]: newValues });
  };

  const anxietyLevels = [
    { value: 1, label: 'Very Low', description: 'Rarely feel anxious or worried', color: 'bg-success' },
    { value: 2, label: 'Low', description: 'Occasionally feel anxious but manageable', color: 'bg-success' },
    { value: 3, label: 'Moderate', description: 'Sometimes feel anxious, affects daily life', color: 'bg-warning' },
    { value: 4, label: 'High', description: 'Frequently feel anxious, significantly impacts life', color: 'bg-error' },
    { value: 5, label: 'Very High', description: 'Constantly anxious, severely impacts daily functioning', color: 'bg-error' }
  ];

  const fearConcerns = [
    { value: 'getting-covid', label: 'Getting COVID-19', icon: 'Shield' },
    { value: 'family-getting-covid', label: 'Family/friends getting COVID-19', icon: 'Users' },
    { value: 'health-complications', label: 'Serious health complications', icon: 'Heart' },
    { value: 'job-security', label: 'Job security or financial stability', icon: 'DollarSign' },
    { value: 'social-isolation', label: 'Continued social isolation', icon: 'UserX' },
    { value: 'future-uncertainty', label: 'Uncertainty about the future', icon: 'HelpCircle' },
    { value: 'healthcare-access', label: 'Access to healthcare', icon: 'Hospital' },
    { value: 'mental-health', label: 'Mental health deteriorating', icon: 'Brain' },
    { value: 'relationship-strain', label: 'Relationship strain or conflict', icon: 'HeartHandshake' },
    { value: 'education-disruption', label: 'Education or career disruption', icon: 'GraduationCap' },
    { value: 'none', label: 'None of the above concerns', icon: 'Check' }
  ];

  const stressConcerns = [
    { value: 'work-stress', label: 'Work-related stress or pressure' },
    { value: 'financial-stress', label: 'Financial difficulties or concerns' },
    { value: 'family-stress', label: 'Family or relationship stress' },
    { value: 'health-stress', label: 'Personal health concerns' },
    { value: 'caregiving-stress', label: 'Caregiving responsibilities' },
    { value: 'academic-stress', label: 'Academic or educational pressure' },
    { value: 'social-stress', label: 'Social anxiety or isolation' },
    { value: 'news-stress', label: 'Overwhelming news or information' },
    { value: 'routine-disruption', label: 'Disrupted routines or normalcy' },
    { value: 'decision-fatigue', label: 'Decision fatigue or overwhelm' }
  ];

  const anxietySymptoms = [
    { value: 'racing-thoughts', label: 'Racing thoughts or mind won\'t stop' },
    { value: 'restlessness', label: 'Feeling restless or on edge' },
    { value: 'concentration-issues', label: 'Difficulty concentrating' },
    { value: 'irritability', label: 'Increased irritability or mood swings' },
    { value: 'muscle-tension', label: 'Muscle tension or aches' },
    { value: 'panic-attacks', label: 'Panic attacks or intense fear episodes' },
    { value: 'avoidance', label: 'Avoiding situations or activities' },
    { value: 'perfectionism', label: 'Increased perfectionism or control needs' },
    { value: 'catastrophizing', label: 'Expecting the worst outcomes' },
    { value: 'physical-symptoms', label: 'Physical symptoms (sweating, trembling)' }
  ];

  return (
    <div className="space-y-8">
      {/* Anxiety Level Rating */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          How would you rate your overall anxiety level during the past two weeks? *
        </label>
        <div className="space-y-3">
          {anxietyLevels.map((level) => (
            <label
              key={level.value}
              className={`
                relative flex items-start p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.anxietyLevel === level.value
                  ? 'border-primary bg-primary-50' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="anxietyLevel"
                value={level.value}
                checked={data.anxietyLevel === level.value}
                onChange={(e) => handleInputChange('anxietyLevel', parseInt(e.target.value))}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 mt-1 flex items-center justify-center
                ${data.anxietyLevel === level.value
                  ? 'border-primary bg-primary' :'border-border'
                }
              `}>
                {data.anxietyLevel === level.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-1">
                  <span className="font-medium text-text-primary">{level.value}. {level.label}</span>
                  <div className={`w-3 h-3 rounded-full ${level.color}`} />
                </div>
                <p className="text-sm text-text-secondary">{level.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Fear and Concerns */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          What are your main fears or concerns related to the current situation?
        </label>
        <p className="text-sm text-text-secondary mb-4">
          Select all that apply. Understanding your specific concerns helps us provide targeted support.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fearConcerns.map((concern) => (
            <label
              key={concern.value}
              className={`
                flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${(data.fearConcerns || []).includes(concern.value)
                  ? 'border-secondary bg-secondary-50 text-secondary-600' :'border-border bg-surface hover:bg-surface-secondary'
                }
              `}
            >
              <input
                type="checkbox"
                checked={(data.fearConcerns || []).includes(concern.value)}
                onChange={(e) => handleMultiSelectChange('fearConcerns', concern.value, e.target.checked)}
                className="sr-only"
              />
              <div className={`
                w-5 h-5 border-2 rounded mr-3 flex items-center justify-center
                ${(data.fearConcerns || []).includes(concern.value)
                  ? 'border-secondary bg-secondary' :'border-border'
                }
              `}>
                {(data.fearConcerns || []).includes(concern.value) && (
                  <Icon name="Check" size={12} color="white" />
                )}
              </div>
              <Icon name={concern.icon} size={16} className="mr-2 flex-shrink-0" />
              <span className="text-sm font-medium">{concern.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Stress Concerns */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          What are your main sources of stress?
        </label>
        <div className="space-y-2">
          {stressConcerns.map((stress) => (
            <label
              key={stress.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.stressConcerns || []).includes(stress.value)}
                onChange={(e) => handleMultiSelectChange('stressConcerns', stress.value, e.target.checked)}
                className="w-4 h-4 text-accent border-border rounded focus:ring-accent focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{stress.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Anxiety Symptoms */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          Which anxiety symptoms are you experiencing?
        </label>
        <div className="space-y-2">
          {anxietySymptoms.map((symptom) => (
            <label
              key={symptom.value}
              className="flex items-center p-3 border border-border rounded-lg hover:bg-surface-secondary cursor-pointer transition-all duration-150"
            >
              <input
                type="checkbox"
                checked={(data.anxietySymptoms || []).includes(symptom.value)}
                onChange={(e) => handleMultiSelectChange('anxietySymptoms', symptom.value, e.target.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary focus:ring-2"
              />
              <span className="ml-3 text-sm text-text-primary">{symptom.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Anxiety Impact */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-3">
          How much does anxiety interfere with your daily activities?
        </label>
        <div className="space-y-2">
          {[
            { value: 'not-at-all', label: 'Not at all - I can function normally' },
            { value: 'slightly', label: 'Slightly - Minor interference with daily tasks' },
            { value: 'moderately', label: 'Moderately - Some activities are difficult' },
            { value: 'severely', label: 'Severely - Many activities are very difficult' },
            { value: 'extremely', label: 'Extremely - Unable to perform most activities' }
          ].map((impact) => (
            <label
              key={impact.value}
              className={`
                relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-150
                ${data.anxietyImpact === impact.value
                  ? 'border-accent bg-accent-50 text-accent-600' :'border-border bg-surface hover:border-border-focus'
                }
              `}
            >
              <input
                type="radio"
                name="anxietyImpact"
                value={impact.value}
                checked={data.anxietyImpact === impact.value}
                onChange={(e) => handleInputChange('anxietyImpact', e.target.value)}
                className="sr-only"
              />
              <div className={`
                w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center
                ${data.anxietyImpact === impact.value
                  ? 'border-accent bg-accent' :'border-border'
                }
              `}>
                {data.anxietyImpact === impact.value && (
                  <div className="w-2 h-2 rounded-full bg-white" />
                )}
              </div>
              <span className="text-sm font-medium">{impact.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Additional Anxiety Details */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Additional Details About Your Anxiety (Optional)
        </label>
        <textarea
          value={data.anxietyDetails || ''}
          onChange={(e) => handleInputChange('anxietyDetails', e.target.value)}
          placeholder="Share any additional information about your anxiety, specific triggers, or patterns you've noticed..."
          rows={4}
          className="form-input resize-none"
        />
      </div>

      {/* Mental Health Resources */}
      <div className="bg-accent-50 border border-accent-100 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Heart" size={20} color="#7C3AED" className="mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-medium text-accent-600 mb-2">
              Remember: You're Not Alone
            </h3>
            <p className="text-sm text-accent-600 mb-2">
              Anxiety during uncertain times is a normal response. Seeking help is a sign of strength, not weakness.
            </p>
            <p className="text-sm text-accent-600">
              If you're experiencing severe anxiety or thoughts of self-harm, please reach out to a mental health professional or crisis helpline immediately.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnxietyAssessmentSection;